import { AutocompleteEntry } from "Components/Autocomplete/AutocompleteEntry";
import DescriptionInput from "Components/Common/Input/TextBoxes/DescriptionInput/DescriptionInput";
import NumericTextbox from "Components/Common/Input/TextBoxes/NumericTextbox";
import TitleInput from "Components/Common/Input/TextBoxes/TitleInput/TitleInput";
import MediaUpload from "Components/Common/MediaUpload/MediaUpload";
import Popover from "Components/Common/Popover/Popover";
import PopoverPane from "Components/Common/Popover/PopoverPane/PopoverPane";
import { TaskTemplateTagSelection } from "Components/TaskTemplateTagSelection/TaskTemplateTagSelection";
import { AuditItemTemplate } from "Models/Audit/AuditItemTemplate";
import { TaskTemplateTag } from "Models/Tasks/TaskTemplateTag";
import * as React from "react";
import { connect } from "react-redux";
import { Action, Dispatch } from "redux";
import ServiceProvider from "ServiceProvider/ServiceProvider";
import Types from "ServiceProvider/Types";
import { IApplicationState } from "Store/Store";
import { ITaskTemplateAction } from "Store/TaskTemplates/TaskTemplateAction";
import { ITaskTemplateState } from "Store/TaskTemplates/TaskTemplateState";
import i18n from "Translations/i18n";
import styles from "./AuditItemsPopover.scss";

interface IProps {
    taskTemplateState: ITaskTemplateState;
    items: AuditItemTemplate[];
    showItemsPopover: boolean;
    getCompanyTaskTemplateTags: () => Action;
    closePopover: () => void;
    onUpdate: (items: AuditItemTemplate[]) => void;
    addItem: () => void;
    popoverPaneIndex: number;
}

interface IState {
    items: AuditItemTemplate[];
    showItemsPopover: boolean;
    popoverPaneIndex: number;
    descriptionFileUrl?: string;
    showAutocomplete: boolean;
    autocompleteEntries?: AutocompleteEntry[];
}

const mapStateToProps = (state: IApplicationState) => ({
    taskTemplateState: state.taskTemplateState
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getCompanyTaskTemplateTags: () => {
        const taskTemplateAction: ITaskTemplateAction = ServiceProvider.get<ITaskTemplateAction>(Types.ITaskTemplateAction);
        return dispatch(taskTemplateAction.getTaskTemplateTagsForCompanyAsync());
    }
});

export class AuditItemsPopover extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            showAutocomplete: false,
            autocompleteEntries: undefined,
            items: props.items,
            showItemsPopover: props.showItemsPopover,
            popoverPaneIndex: props.popoverPaneIndex
        };

        this.onMediaChanged = this.onMediaChanged.bind(this);
        this.deleteItem = this.deleteItem.bind(this);
        this.onIndexChange = this.onIndexChange.bind(this);
        this.itemWeightChanged = this.itemWeightChanged.bind(this);
        this.onUpdateTags = this.onUpdateTags.bind(this);

        this.props.getCompanyTaskTemplateTags();
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        const partialState: Partial<IState> = {};

        if (prevState.showItemsPopover !== nextProps.showItemsPopover) {
            partialState.showItemsPopover = !!nextProps.showItemsPopover;
        }

        if (!prevState.showItemsPopover && prevState.showItemsPopover !== nextProps.showItemsPopover) {
            partialState.popoverPaneIndex = nextProps.popoverPaneIndex;
        }

        if (nextProps.items && nextProps.items !== prevState.items) {
            partialState.items = nextProps.items;
        }

        return partialState;
    }

    public onNameUpdated(itemIndex: number, newValue: string) {
        const items = [...this.state.items];
        items[itemIndex] = { ...items[itemIndex], name: newValue };
        this.setState({ items });
        this.props.onUpdate(items);
    }

    public onDescriptionUpdated(itemIndex: number, newValue: string) {
        const items = [...this.state.items];
        items[itemIndex] = { ...items[itemIndex], description: newValue };
        this.setState({ items });
        this.props.onUpdate(items);
    }

    public onMediaChanged(itemIndex: number, state: { imageFile?: File, videoFile?: File, videoThumbnailFile?: File }) {
        const items = [...this.state.items];
        items[itemIndex] = {
            ...items[itemIndex],
            pictureUrl: state.imageFile ? URL.createObjectURL(state.imageFile) : undefined,
            videoUrl: state.videoFile ? state.videoFile !== items[itemIndex].videoFile ? URL.createObjectURL(state.videoFile) : items[itemIndex].videoUrl : state.videoThumbnailFile ? items[itemIndex].videoUrl : undefined,
            videoThumbnailUrl: state.videoThumbnailFile ? URL.createObjectURL(state.videoThumbnailFile) : undefined,
            pictureFile: state.imageFile,
            videoFile: state.videoFile,
            videoThumbnailFile: state.videoThumbnailFile
        };
        this.setState({ items });
        this.props.onUpdate(items);
    }

    public deleteItem(itemNumber: number) {
        const localItems = this.state.items;
        localItems.splice(itemNumber - 1, 1);
        const paneIndex = localItems.length - 1;

        this.setState({
            items: localItems,
            showItemsPopover: true,
            popoverPaneIndex: paneIndex
        });

        this.props.onUpdate(localItems);
    }

    public itemWeightChanged(newValue: number) {
        const items = [...this.state.items];
        items[this.state.popoverPaneIndex] = { ...items[this.state.popoverPaneIndex], weight: newValue };
        this.setState({ items });
        this.props.onUpdate(items);
    }

    public onIndexChange(itemIndex: number) {
        this.setState({
            popoverPaneIndex: itemIndex
        });
    }

    public onUpdateTags(itemIndex: number, newTags?: TaskTemplateTag[]) {
        const items = [...this.state.items];
        items[itemIndex] = {
            ...items[itemIndex],
            taskTemplateTags: newTags ? newTags : []
        };
        this.setState({ items });
        this.props.onUpdate(items);
    }

    public render() {
        return (
            <Popover
                title={i18n.t("audits.auditItem")}
                isOpen={this.state.showItemsPopover}
                onClose={this.props.closePopover}
                paneIndex={this.state.popoverPaneIndex}
                addChild={this.props.addItem}
                deleteChild={this.deleteItem}
                onIndexChange={this.onIndexChange}
                addItemText={i18n.t("audits.addAuditItem")}
                additionalHeaderControl={(
                    <span>
                        <span className={styles.numericTextBoxLabel}>{i18n.t("audits.questions.weight")}:</span>
                        <NumericTextbox className={styles.numericTextBox} onChange={this.itemWeightChanged} value={this.state.items[this.state.popoverPaneIndex] && this.state.items[this.state.popoverPaneIndex].weight ? this.state.items[this.state.popoverPaneIndex].weight.toFixed(2) : 0} min={1} />
                    </span>
                )}
            >
                {
                    this.state.items.map((item, itemIndex) => (
                        <PopoverPane key={itemIndex}>
                            <MediaUpload
                                imageUrl={item.pictureUrl}
                                videoUrl={item.videoUrl}
                                videoThumbnailUrl={item.videoThumbnailUrl}
                                onChange={(state) => this.onMediaChanged(itemIndex, state)}
                            />
                            <TitleInput value={item.name} maxHeight={50} onChange={(newValue) => this.onNameUpdated(itemIndex, newValue)} placeholder={i18n.t("audits.placeholders.auditItemTitle")} />
                            <DescriptionInput value={item.description} maxHeight={100} onChange={(newValue) => this.onDescriptionUpdated(itemIndex, newValue)} placeholder={i18n.t("audits.placeholders.auditItemDescription")} />
                            <TaskTemplateTagSelection taskTemplateState={this.props.taskTemplateState} onSelectionChanged={(newTaskTemplateTags) => this.onUpdateTags(itemIndex, newTaskTemplateTags)} selectedTaskTemplateTags={item.taskTemplateTags} />
                        </PopoverPane>
                    ))
                }
            </Popover>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(AuditItemsPopover);


// WEBPACK FOOTER //
// ./src/Components/Pages/AuditTemplateDetailView/AuditItems/AuditItemsPopover/AuditItemsPopover.tsx